// src/components/Layout/Layout.tsx
import React, { useState, useEffect } from "react";
import { Box, useMediaQuery, useTheme, PaletteMode } from "@mui/material";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import theme from "../../theme";

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 260;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [darkMode, setDarkMode] = useState<PaletteMode>("light");
  const defaultTheme = useTheme();
  const isMobile = useMediaQuery(defaultTheme.breakpoints.down("md"));

  // Close sidebar on mobile by default
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  // Create dynamic theme based on dark mode preference
  // Create dynamic theme based on dark mode preference
  const darkTheme = React.useMemo(
    () =>
      createTheme({
        ...theme,
        palette: {
          ...theme.palette, // Keep other palette settings
          mode: darkMode, // Set mode only once
          ...(darkMode === "dark"
            ? {
                background: {
                  default: "#121212",
                  paper: "#1e1e1e",
                },
              }
            : {}), // Empty object if light mode to use theme defaults
        },
      }),
    [darkMode]
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ display: "flex", height: "100vh" }}>
        <Header
          toggleSidebar={toggleSidebar}
          darkMode={darkMode === "dark"}
          toggleDarkMode={toggleDarkMode}
        />
        <Sidebar open={sidebarOpen} onClose={toggleSidebar} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            mt: 8,
            transition: defaultTheme.transitions.create("margin", {
              easing: defaultTheme.transitions.easing.sharp,
              duration: defaultTheme.transitions.duration.leavingScreen,
            }),
            marginLeft: sidebarOpen ? `${drawerWidth}px` : 0,
            ...(isMobile && {
              marginLeft: 0,
            }),
            overflow: "auto",
            height: "calc(100vh - 64px)",
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
